import { inject, Injectable } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { InfoModalComponent } from '../../../shared/sub-features/modal-feature-info/info-modal.component';
import { UserStateService } from '../../../state/user/user-state';
import { Address } from '../../../core/classes/Address';
import { SnackBarService } from '../../../core/services/snack-bar.service';
import { environment } from '../../../../environments/environment';
import Geocoder = google.maps.Geocoder;
import LatLng = google.maps.LatLng;
import AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken;
import AutocompleteService = google.maps.places.AutocompleteService;
import PlacesServiceStatus = google.maps.places.PlacesServiceStatus;
import GeocoderStatus = google.maps.GeocoderStatus;

@Injectable({
	providedIn: 'root',
})
export class AddressService {
	sessionToken: AutocompleteSessionToken = null;

	private userStateSvc = inject(UserStateService);
	private modalCtrl = inject(ModalController);
	private platform = inject(Platform);
	private snackBarSvc = inject(SnackBarService);

	constructor() {}

	async ifPermissionCanBeRequested() {
		try {
			const { Geolocation } = await import('@capacitor/geolocation');
			const { location } = await Geolocation.checkPermissions();

			return location !== 'denied' && location !== 'granted';
		} catch {
			return false;
		}
	}

	async initUserPosition(): Promise<any> {
		let coordinates = null;

		if (this.platform.is('capacitor')) {
			const { Geolocation } = await import('@capacitor/geolocation');

			const res = await Geolocation.requestPermissions().catch(async err => {
				console.log('ERRR', err);
				if (err?.code === 'OS-PLUG-GLOC-0007') {
					const res = await InfoModalComponent.show(this.modalCtrl, {
						title: 'maps.geo',
						info: 'maps.geo_off',
						mainButton: 'words.retry',
						closeButton: 'words.skip',
					});

					if (res) return this.initUserPosition();
				}

				return this.userStateSvc.address$.next(null);
			});

			if (res?.location !== 'granted') {
				this.snackBarSvc.present('maps.errors.enable_geo');
				return this.userStateSvc.address$.next(null);
			}

			coordinates = (await Geolocation.getCurrentPosition()).coords;
		} else {
			coordinates = await new Promise((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(
					e => resolve(e.coords),
					e => resolve(null)
				);
			});
			if (!coordinates) {
				this.snackBarSvc.present('maps.errors.enable_geo');
				return this.userStateSvc.address$.next(null);
			}
		}

		const a = await this.getAddressByCoords(coordinates.latitude, coordinates.longitude);
		this.userStateSvc.address$.next(a || null);
		return a || null;
	}

	async getAddressByCoords(lat: number, lng: number): Promise<Address | null> {
		const geocoder = new Geocoder();
		const response = await geocoder.geocode({
			location: new LatLng(lat, lng),
		});

		const place =
			response.results.find(r => r.address_components.find(aC => aC.types.includes('street_number'))) ||
			response.results[0];
		return place ? Address.fromGeocode(place) : null;
	}

	async getAddressByPrediction(placeId: string): Promise<Address | null> {
		return new Promise((resolve, reject) => {
			new Geocoder().geocode({ placeId }, (result, status) => {
				if (status === GeocoderStatus.OK && result.length > 0) {
					resolve(Address.fromGeocode(result[0]));
				} else {
					reject(status);
				}
			});
		});
	}

	async getAddressBySearchString(address: string): Promise<Address | null> {
		return new Promise((resolve, reject) => {
			const service = new Geocoder();
			service.geocode({ address: address }, (result, status) => {
				if (status === GeocoderStatus.OK && result.length > 0) {
					resolve(Address.fromGeocode(result[0]));
					return;
				} else {
					reject(status);
					return;
				}
			});
		});
	}

	async listenLocationPermissionChanges() {
		if (!navigator.permissions) return;

		const webPermissionStatus = await navigator.permissions.query({
			name: 'geolocation' as PermissionName,
		});
		webPermissionStatus.onchange = () => {
			if (webPermissionStatus.state === 'granted') {
				this.initUserPosition();
			}
		};
	}

	async loadPredictions(input: string) {
		if (!this.sessionToken) this.sessionToken = new AutocompleteSessionToken();

		const service = new AutocompleteService();
		return (
			await service.getPlacePredictions(
				{
					input,
					sessionToken: this.sessionToken,
					componentRestrictions: { country: environment.countryList },
					types: /\d/.test(input) ? ['address'] : ['geocode'],
				},
				(predictions, status) => {
					return status === PlacesServiceStatus.OK && predictions ? predictions : [];
				}
			)
		).predictions;
	}
}
